import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { oneDark } from "@codemirror/theme-one-dark";
import { githubLight } from "@uiw/codemirror-theme-github";
import {
  DndContext, PointerSensor, closestCenter, useSensor, useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import {
  api,
  type Flow,
  type FlowRun,
  type FlowRunStep,
  type FlowStep,
  type FlowAssertion,
  type AssertOp,
  type KV,
  type RequestSummary,
} from "../lib/api";
import { InlineLoading, LoadingButton } from "./Spinner";
import RelativeTime from "./RelativeTime";
import ScheduleModal from "./ScheduleModal";
import VarText from "./VarText";
import { useConfirm } from "./ConfirmDialog";
import { useToast } from "./Toast";
import CodeViewer from "./CodeViewer";
import Markdown from "./Markdown";
import { useResizable } from "../lib/useResizable";
import { closeOpenTabFor } from "../lib/openTabs";

type Tab = "step" | "run" | "history" | "docs";

interface RunOutcome { run: FlowRun; steps: FlowRunStep[] }

export default function FlowBuilder({ flowId, workspaceId }: { flowId: string; workspaceId: string }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();
  const detailQ = useQuery({ queryKey: ["flow", flowId], queryFn: () => api.flows.get(flowId) });
  const data = detailQ.data?.flow ?? null;
  const envsQ = useQuery({ queryKey: ["environments", workspaceId], queryFn: () => api.environments.list(workspaceId) });

  const [name, setName] = useState("");
  const [docs, setDocs] = useState("");
  const [steps, setSteps] = useState<FlowStep[]>([]);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const [sp, setSp] = useSearchParams();
  const tab: Tab = ((): Tab => {
    const q = sp.get("tab") ?? "";
    const valid: Tab[] = ["step", "run", "history", "docs"];
    return (valid as string[]).includes(q) ? (q as Tab) : "step";
  })();
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(sp);
    if (t === "step") next.delete("tab"); else next.set("tab", t);
    setSp(next, { replace: true });
  };

  // Last run shown on the Run tab (live update at end of /run).
  const [lastRun, setLastRun] = useState<RunOutcome | null>(null);
  // Historical run opened from the History tab — portaled into a modal
  // so the Run tab stays focused on the most-recent live run.
  const [historyRun, setHistoryRun] = useState<RunOutcome | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  // Pull the latest server name on every refetch — rename mutations
  // hit the API directly and invalidate this query.
  useEffect(() => {
    if (data?.name) setName(data.name);
  }, [data?.name]);

  // Reset local state whenever a different flow loads.
  useEffect(() => {
    if (!data) return;
    setName(data.name);
    setDocs(data.docs ?? "");
    setSteps(data.steps ?? []);
    setSelectedStepId((data.steps ?? [])[0]?.id ?? null);
    setSavedAt(data.updated_at);
    setLastRun(null);
  }, [data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!name) return;
    const prev = document.title;
    document.title = `${name} · API Stash`;
    return () => { document.title = prev; };
  }, [name]);

  const isDirty = useMemo(() => {
    if (!data) return false;
    // JSON.stringify is sensitive to property *order* — a save can
    // succeed but the server's response keys in a different order
    // than the client built them, leaving the dirty flag stuck on.
    // Sort keys consistently before comparing.
    return (
      data.name !== name ||
      (data.docs ?? "") !== docs ||
      stableStringify(data.steps) !== stableStringify(steps)
    );
  }, [data, name, docs, steps]);

  const save = useMutation({
    mutationFn: () => api.flows.update(flowId, { name, docs, steps }),
    onSuccess: () => {
      setSavedAt(new Date().toISOString());
      qc.invalidateQueries({ queryKey: ["flow", flowId] });
      if (data?.collection_id) qc.invalidateQueries({ queryKey: ["flows", data.collection_id] });
    },
  });

  const envId = sp.get("env") || envsQ.data?.environments.find((e) => e.is_default)?.id || "";

  const run = useMutation({
    mutationFn: async () => {
      if (isDirty) await save.mutateAsync();
      return api.flows.run(flowId, { environmentId: envId || undefined });
    },
    onSuccess: (r) => {
      setLastRun(r);
      qc.invalidateQueries({ queryKey: ["flow-runs", flowId] });
    },
  });

  const remove = useMutation({
    mutationFn: () => api.flows.remove(flowId),
    onSuccess: () => {
      if (data?.collection_id) qc.invalidateQueries({ queryKey: ["flows", data.collection_id] });
      const { search, nextActive } = closeOpenTabFor(workspaceId, `f:${flowId}`);
      const path = nextActive
        ? (nextActive.startsWith("w:") ? `/w/${nextActive.slice(2)}`
          : nextActive.startsWith("f:") ? `/f/${nextActive.slice(2)}`
          : nextActive.startsWith("r:") ? `/r/${nextActive.slice(2)}`
          : `/r/${nextActive}`)
        : "/";
      window.location.assign(path + search);
    },
  });

  const { size: stepsWidth, dragProps: stepsDrag } = useResizable({
    axis: "x", initial: 300, min: 200, max: 520, storageKey: "rest.flow.stepsWidth",
  });

  if (detailQ.isLoading) return <div className="grid h-full place-items-center"><InlineLoading label="Loading flow…" /></div>;
  if (!data) return <div className="grid h-full place-items-center muted">Flow not found.</div>;

  const selectedStep = steps.find((s) => s.id === selectedStepId) ?? null;
  const selectedStepIdx = selectedStep ? steps.indexOf(selectedStep) : -1;

  // Build a quick map of step-status from the last run so the steps
  // pane can show a tiny status pill next to each row.
  const lastRunStatusByStepId: Record<string, FlowRunStep | undefined> = {};
  for (const s of lastRun?.steps ?? []) {
    lastRunStatusByStepId[s.step_id] = s;
  }

  const replaceStep = (next: FlowStep) => {
    setSteps((prev) => prev.map((s) => (s.id === next.id ? next : s)));
  };
  const deleteStep = (id: string) => {
    setSteps((prev) => {
      const next = prev.filter((s) => s.id !== id);
      if (selectedStepId === id) setSelectedStepId(next[0]?.id ?? null);
      return next;
    });
  };
  const duplicateStep = (id: string) => {
    setSteps((prev) => {
      const idx = prev.findIndex((s) => s.id === id);
      if (idx < 0) return prev;
      const copy = JSON.parse(JSON.stringify(prev[idx])) as FlowStep;
      copy.id = newStepId();
      copy.name = `${copy.name} (copy)`;
      setSelectedStepId(copy.id);
      return [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)];
    });
    setTab("step");
  };
  // Catch the cheap mistakes before dispatching a run: a saved-request
  // step with nothing picked, an inline step with no URL, extract rows
  // missing a name or path. Returns human messages; empty = good to go.
  const validateSteps = (): string[] => {
    const errors: string[] = [];
    steps.forEach((st, i) => {
      if (st.disabled) return;
      const label = `Step ${i + 1} "${st.name}"`;
      if (st.kind === "request") {
        if (st.source === "saved" && !st.requestId) errors.push(`${label}: no saved request selected.`);
        if (st.source === "inline" && !(st.url ?? "").trim()) errors.push(`${label}: URL is empty.`);
        (st.extract ?? []).forEach((ex, j) => {
          if ((ex.as || ex.from) && (!ex.as.trim() || !ex.from.trim())) {
            errors.push(`${label}: extract #${j + 1} needs both a name and a path.`);
          }
        });
      }
      if (st.kind === "script" && !(st.source ?? "").trim()) errors.push(`${label}: script is empty.`);
    });
    return errors;
  };

  const addStep = (kind: FlowStep["kind"]) => {
    const id = newStepId();
    let step: FlowStep;
    if (kind === "request") step = { id, kind, name: "New request", source: "saved", onFailure: "stop" };
    else if (kind === "script") step = { id, kind, name: "New script", source: "// ctx, env, steps, log\n" };
    else step = { id, kind: "delay", name: "Delay", ms: 500 };
    setSteps((prev) => [...prev, step]);
    setSelectedStepId(id);
    setTab("step");
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="border-b border-border bg-surface px-3 py-1.5 flex items-center gap-2">
        <div
          className="flex-1 min-w-0 truncate text-sm font-medium text-text"
          title={name || "Untitled flow"}
        >{name || "Untitled flow"}</div>
        {isDirty && <span className="chip bg-warn/15 text-warn">Unsaved</span>}
        {!isDirty && savedAt && <RelativeTime iso={savedAt} className="text-xs muted-strong" prefix="Saved " />}
        <LoadingButton className="btn" pending={save.isPending} disabled={!isDirty} onClick={() => save.mutate()}>Save</LoadingButton>
        <LoadingButton
          className="btn btn-primary"
          pending={run.isPending}
          disabled={steps.filter((s) => !s.disabled).length === 0}
          onClick={() => {
            const errors = validateSteps();
            if (errors.length) {
              toast.error(errors[0] + (errors.length > 1 ? ` (+${errors.length - 1} more)` : ""));
              setTab("step");
              return;
            }
            setTab("run");
            run.mutate();
          }}
        >Run</LoadingButton>
        <button
          type="button"
          className="btn"
          onClick={() => setScheduleOpen(true)}
          title="Run this flow on a schedule"
        >Schedule…</button>
        <button
          type="button"
          className="btn"
          onClick={async () => {
            const ok = await confirm({ title: `Delete "${data.name}"?`, description: "This permanently removes the flow and every run.", danger: true });
            if (ok) remove.mutate();
          }}
        >Delete</button>
      </div>
      <ScheduleModal
        workspaceId={workspaceId}
        flowId={flowId}
        flowName={name || "Untitled flow"}
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
      />

      <div className="flex-1 min-h-0 flex">
        {/* Left: steps list. */}
        <aside className="shrink-0 border-r border-border bg-surface flex flex-col min-h-0" style={{ width: stepsWidth }}>
          <div className="h-9 px-3 flex items-center justify-between border-b border-border">
            <div className="text-xs uppercase tracking-wider muted-strong font-semibold">Steps · {steps.length}</div>
            <AddStepMenu onAdd={addStep} />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto scroll-pane">
            <StepsList
              steps={steps}
              selectedStepId={selectedStepId}
              onSelect={(id) => { setSelectedStepId(id); setTab("step"); }}
              onReorder={(next) => setSteps(next)}
              onToggle={(id) => setSteps((prev) => prev.map((s) => s.id === id ? { ...s, disabled: !s.disabled } : s))}
              onDelete={deleteStep}
              onDuplicate={duplicateStep}
              statusByStepId={lastRunStatusByStepId}
            />
          </div>
        </aside>
        <div {...stepsDrag} role="separator" aria-orientation="vertical" className="w-1.5 cursor-col-resize bg-transparent hover:bg-accent active:bg-accent-bright transition-colors shrink-0" aria-label="Resize steps pane" />

        {/* Right: detail pane. */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <div className="border-b border-border bg-surface px-3 flex items-center gap-2 overflow-x-auto">
            <Tabs current={tab} onChange={setTab} />
          </div>

          <div className="flex-1 min-h-0 overflow-auto scroll-pane p-3">
            {tab === "step" && (
              selectedStep ? (
                <StepEditor
                  workspaceId={workspaceId}
                  collectionId={data.collection_id}
                  step={selectedStep}
                  stepIndex={selectedStepIdx}
                  onChange={replaceStep}
                  lastRunStep={lastRunStatusByStepId[selectedStep.id]}
                  priorVars={priorExtractVars(steps, selectedStep.id)}
                />
              ) : (
                <FlowWelcome onAdd={addStep} hasSteps={steps.length > 0} />
              )
            )}
            {tab === "run" && (
              <RunPane outcome={lastRun} pending={run.isPending} error={run.error ? String((run.error as Error).message) : null} steps={steps} />
            )}
            {tab === "history" && (
              <HistoryPane flowId={flowId} onOpen={(r) => setHistoryRun(r)} />
            )}
            {tab === "docs" && (
              <DocsEditor docs={docs} setDocs={setDocs} />
            )}
          </div>
        </div>
      </div>
      <FlowRunDetailModal outcome={historyRun} steps={steps} onClose={() => setHistoryRun(null)} />
    </div>
  );
}

// Portaled modal for inspecting any historical run, opened from the
// History tab. Esc + backdrop click close. Re-uses RunOutcomeView so
// the contents track the inline Run-tab layout.
function FlowRunDetailModal({ outcome, steps, onClose }: { outcome: RunOutcome | null; steps: FlowStep[]; onClose: () => void }) {
  useEffect(() => {
    if (!outcome) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [outcome, onClose]);
  if (!outcome) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[110] grid place-items-center p-4"
      style={{ background: "var(--backdrop)" }}
      onClick={onClose}
    >
      <div
        className="card w-full max-w-5xl h-[min(820px,90vh)] overflow-hidden flex flex-col shadow-[var(--shadow-strong)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-border px-4 py-2.5">
          <h2 className="text-sm font-semibold">Run details</h2>
          <span className="text-xs muted-strong">
            {outcome.run.started_at && new Date(outcome.run.started_at).toLocaleString()}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
          >×</button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-4">
          <RunOutcomeView outcome={outcome} steps={steps} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Steps list ───────────────────────────────────────────────────────────

function StepsList({ steps, selectedStepId, onSelect, onReorder, onToggle, onDelete, onDuplicate, statusByStepId }: {
  steps: FlowStep[];
  selectedStepId: string | null;
  onSelect: (id: string) => void;
  onReorder: (next: FlowStep[]) => void;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  statusByStepId: Record<string, FlowRunStep | undefined>;
}) {
  const ids = useMemo(() => steps.map((s) => s.id), [steps]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const from = ids.indexOf(String(e.active.id));
    const to = ids.indexOf(String(e.over.id));
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(steps, from, to));
  };
  if (steps.length === 0) {
    return (
      <div className="p-3">
        <div className="empty-card">
          No steps yet — add one with the <span className="empty-card-strong">+</span> button above.
          Steps run top to bottom; drag to reorder.
        </div>
      </div>
    );
  }
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ol className="py-1">
          {steps.map((s, i) => (
            <SortableStepRow
              key={s.id}
              index={i + 1}
              step={s}
              selected={s.id === selectedStepId}
              status={statusByStepId[s.id]}
              onSelect={() => onSelect(s.id)}
              onToggle={() => onToggle(s.id)}
              onDelete={() => onDelete(s.id)}
              onDuplicate={() => onDuplicate(s.id)}
            />
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  );
}

function SortableStepRow({ step, index, selected, status, onSelect, onToggle, onDelete, onDuplicate }: {
  step: FlowStep;
  index: number;
  selected: boolean;
  status: FlowRunStep | undefined;
  onSelect: () => void;
  onToggle: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id: step.id });
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.6 : step.disabled ? 0.5 : undefined,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={
        "group flex items-center gap-2 px-2 py-1.5 mx-1 rounded cursor-pointer min-w-0 " +
        (selected ? "bg-accent-soft text-accent-bright" : "hover:bg-surface-2 text-text")
      }
      onClick={onSelect}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="shrink-0 text-text-faint hover:text-text cursor-grab active:cursor-grabbing"
        title="Drag to reorder"
        onClick={(e) => e.stopPropagation()}
      >⋮⋮</button>
      <span className="chip bg-surface-3 muted-strong mono shrink-0 text-[10px]">{index}</span>
      <StepKindIcon kind={step.kind} />
      <span className="flex-1 min-w-0 truncate text-sm">{step.name}</span>
      {step.disabled && <span className="chip bg-surface-3 muted-strong shrink-0 text-[10px]">off</span>}
      {status && <StepStatusDot status={status.status} />}
      <button
        type="button"
        className="h-5 w-5 grid place-items-center rounded text-text-faint opacity-0 group-hover:opacity-100 hover:text-text hover:bg-surface-3"
        title="Duplicate step"
        onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
      >⧉</button>
      <button
        type="button"
        className="h-5 w-5 grid place-items-center rounded text-text-faint opacity-0 group-hover:opacity-100 hover:text-text hover:bg-surface-3"
        title={step.disabled ? "Enable step (currently skipped on runs)" : "Disable step (skip it on runs)"}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
      >{step.disabled ? "○" : "●"}</button>
      <button
        type="button"
        className="h-5 w-5 grid place-items-center rounded text-text-faint opacity-0 group-hover:opacity-100 hover:text-danger hover:bg-surface-3"
        title="Delete step"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
      >×</button>
    </li>
  );
}

function StepKindIcon({ kind }: { kind: FlowStep["kind"] }) {
  if (kind === "request") return <span className="chip bg-accent-soft text-accent shrink-0 text-[10px]">REQ</span>;
  if (kind === "script") return <span className="chip bg-warn/20 text-warn shrink-0 text-[10px]">JS</span>;
  return <span className="chip bg-surface-3 muted-strong shrink-0 text-[10px]">WAIT</span>;
}

function StepStatusDot({ status }: { status: FlowRunStep["status"] }) {
  let cls = "bg-text-faint";
  let title = status;
  if (status === "succeeded") cls = "bg-good";
  else if (status === "failed") cls = "bg-danger";
  else if (status === "running") cls = "bg-accent";
  else if (status === "skipped") cls = "bg-text-faint";
  return <span className={"shrink-0 w-2 h-2 rounded-full " + cls} title={title} />;
}

// ── Tabs ────────────────────────────────────────────────────────────────

function Tabs({ current, onChange }: { current: Tab; onChange: (t: Tab) => void }) {
  const items: Array<{ id: Tab; label: string }> = [
    { id: "step", label: "Step" },
    { id: "run", label: "Run" },
    { id: "history", label: "History" },
    { id: "docs", label: "Docs" },
  ];
  return (
    <div className="flex gap-1">
      {items.map((t) => {
        const active = t.id === current;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={
              "px-3 py-2 text-sm font-medium border-b-2 -mb-px " +
              (active ? "border-accent text-text" : "border-transparent text-text-dim hover:text-text")
            }
          >{t.label}</button>
        );
      })}
    </div>
  );
}

// ── Step editor (router by kind) ────────────────────────────────────────

function StepEditor({ workspaceId, collectionId, step, stepIndex, onChange, lastRunStep, priorVars }: {
  workspaceId: string;
  collectionId: string;
  step: FlowStep;
  stepIndex: number;
  onChange: (s: FlowStep) => void;
  lastRunStep: FlowRunStep | undefined;
  priorVars: string[];
}) {
  return (
    <div className="space-y-3 min-w-0">
      <div className="flex items-center gap-2">
        <span className="chip bg-surface-3 muted-strong mono text-[10px]">{stepIndex + 1}</span>
        <input
          className="input flex-1 !text-sm"
          value={step.name}
          onChange={(e) => onChange({ ...step, name: e.target.value })}
          placeholder="Step name"
          aria-label="Step name"
        />
        <label className="flex items-center gap-2 text-xs muted-strong">
          <input
            type="checkbox"
            checked={!step.disabled}
            onChange={(e) => onChange({ ...step, disabled: !e.target.checked })}
          /> Enabled
        </label>
      </div>
      {step.kind === "request" && (
        <RequestStepEditor
          workspaceId={workspaceId}
          collectionId={collectionId}
          step={step}
          onChange={onChange}
          lastRunStep={lastRunStep}
          priorVars={priorVars}
        />
      )}
      {step.kind === "script" && (
        <ScriptStepEditor step={step} onChange={onChange} priorVars={priorVars} />
      )}
      {step.kind === "delay" && (
        <DelayStepEditor step={step} onChange={onChange} />
      )}
    </div>
  );
}

// ── Request step editor ─────────────────────────────────────────────────

function RequestStepEditor({ workspaceId, collectionId, step, onChange, lastRunStep, priorVars }: {
  workspaceId: string;
  collectionId: string;
  step: Extract<FlowStep, { kind: "request" }>;
  onChange: (s: FlowStep) => void;
  lastRunStep: FlowRunStep | undefined;
  priorVars: string[];
}) {
  void workspaceId;
  const requestsQ = useQuery({
    queryKey: ["requests", collectionId],
    queryFn: () => api.collections.requests(collectionId),
  });
  const allRequests: RequestSummary[] = requestsQ.data?.requests ?? [];
  const selectedSavedReq = step.requestId ? allRequests.find((r) => r.id === step.requestId) : null;

  return (
    <div className="space-y-3 min-w-0">
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-xs muted-strong">
          <span className="label">Source</span>
          <select
            value={step.source}
            onChange={(e) => onChange({ ...step, source: e.target.value as "saved" | "inline" })}
            className="input py-1 text-sm"
          >
            <option value="saved">Saved request</option>
            <option value="inline">Inline</option>
          </select>
        </label>
      </div>

      {step.source === "saved" ? (
        <div className="space-y-2">
          <select
            className="input w-full !text-sm"
            value={step.requestId ?? ""}
            onChange={(e) => onChange({ ...step, requestId: e.target.value || undefined })}
          >
            <option value="">— Pick a request —</option>
            {allRequests.map((r) => (
              <option key={r.id} value={r.id}>{r.method} · {r.name}</option>
            ))}
          </select>
          {selectedSavedReq && (
            <div className="text-xs muted flex items-center gap-1.5 min-w-0">
              <span className={"method-tag shrink-0 " + selectedSavedReq.method.toLowerCase()}>{selectedSavedReq.method}</span>
              <VarText value={selectedSavedReq.url} className="mono truncate min-w-0" />
              <a
                href={`/r/${selectedSavedReq.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto shrink-0 text-accent-bright hover:underline"
                title="Open this request in a new tab"
              >Edit request ↗</a>
            </div>
          )}
          {/* For saved requests we only expose URL/method overrides for
              now. Headers/query/body overrides land in a future pass —
              the underlying spec already supports them via `overrides`. */}
          <div className="grid grid-cols-2 gap-2">
            <input
              className="input !text-sm"
              placeholder="Override method"
              value={step.overrides?.method ?? ""}
              onChange={(e) => onChange({ ...step, overrides: { ...(step.overrides ?? {}), method: e.target.value || undefined } })}
            />
            <input
              className="input !text-sm"
              placeholder="Override URL"
              value={step.overrides?.url ?? ""}
              onChange={(e) => onChange({ ...step, overrides: { ...(step.overrides ?? {}), url: e.target.value || undefined } })}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-2">
            <select
              className="input !text-sm"
              value={step.method ?? "GET"}
              onChange={(e) => onChange({ ...step, method: e.target.value })}
            >
              {["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"].map((m) =>
                <option key={m} value={m}>{m}</option>)}
            </select>
            <input
              className="input !text-sm"
              placeholder="https://api.example.com/users"
              value={step.url ?? ""}
              onChange={(e) => onChange({ ...step, url: e.target.value })}
            />
          </div>
          <select
            className="input !text-sm"
            value={step.body_type ?? "none"}
            onChange={(e) => onChange({ ...step, body_type: e.target.value as Extract<FlowStep, { kind: "request" }>["body_type"] })}
          >
            <option value="none">No body</option>
            <option value="json">JSON</option>
            <option value="text">Text</option>
            <option value="urlencoded">Form URL Encoded</option>
          </select>
          {(step.body_type ?? "none") !== "none" && (
            <textarea
              className="input input-mono w-full min-h-[120px] text-xs"
              placeholder={step.body_type === "json" ? '{ "key": "{{token}}" }' : "Body"}
              value={step.body ?? ""}
              onChange={(e) => onChange({ ...step, body: e.target.value })}
              spellCheck={false}
            />
          )}
          <KVRows
            label="Headers"
            rows={step.headers ?? []}
            keyPlaceholder="Authorization"
            valuePlaceholder="Bearer {{token}}"
            onChange={(rows) => onChange({ ...step, headers: rows })}
          />
          <KVRows
            label="Query params"
            rows={step.query ?? []}
            keyPlaceholder="page"
            valuePlaceholder="1"
            onChange={(rows) => onChange({ ...step, query: rows })}
          />
        </div>
      )}

      {priorVars.length > 0 && (
        <p className="text-[11px] muted">
          Available from earlier steps:{" "}
          {priorVars.map((v) => <code key={v} className="mono mr-1">{"{{" + v + "}}"}</code>)}
          — usable in the URL, headers, and body.
        </p>
      )}

      {/* Extract section. */}
      <div className="space-y-1">
        <div className="text-xs uppercase tracking-wider muted-strong font-semibold pt-2">Extract values</div>
        <p className="text-[11px] muted">
          Save parts of this response under a name, then use them in later steps as <code className="mono">{"{{name}}"}</code>.
        </p>
        <details className="text-[11px] muted">
          <summary className="cursor-pointer muted-strong">Path syntax examples</summary>
          <ul className="mt-1 ml-4 list-disc space-y-0.5">
            <li><code className="mono">$.status</code> — the HTTP status code</li>
            <li><code className="mono">$.body.user.id</code> — a nested body field</li>
            <li><code className="mono">$.body[0].name</code> — first array element's field</li>
            <li><code className="mono">$.headers.x-request-id</code> — a response header</li>
          </ul>
        </details>
        <ExtractEditor
          rows={step.extract ?? []}
          onChange={(rows) => onChange({ ...step, extract: rows })}
          lastRunStep={lastRunStep}
        />
        <ExtractPreview lastRunStep={lastRunStep} />
      </div>

      {/* Expect + onFailure. */}
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs">
          <span className="label">Expect status</span>
          <input
            className="input !text-sm mt-1"
            placeholder="e.g. 2xx or 200,201"
            value={step.expect?.status ?? ""}
            onChange={(e) => onChange({ ...step, expect: { status: e.target.value || undefined } })}
          />
          <p className="text-[10px] muted mt-1">
            Exact (<code className="mono">200</code>), wildcard (<code className="mono">2xx</code>), or a list (<code className="mono">200,201</code>). Empty = any status passes.
          </p>
        </label>
        <label className="text-xs">
          <span className="label">If this step fails</span>
          <div className="flex flex-col gap-1.5 mt-1">
            {([
              { v: "stop" as const, label: "Stop the flow", hint: "Remaining steps are skipped." },
              { v: "continue" as const, label: "Continue anyway", hint: "Mark it failed but keep running." },
            ]).map(({ v, label, hint }) => (
              <label key={v} className="flex items-center gap-1.5 text-sm" title={hint}>
                <input
                  type="radio"
                  checked={(step.onFailure ?? "stop") === v}
                  onChange={() => onChange({ ...step, onFailure: v })}
                /> {label}
              </label>
            ))}
          </div>
        </label>
      </div>

      {/* Tests / assertions. */}
      <div className="space-y-1">
        <div className="text-xs uppercase tracking-wider muted-strong font-semibold pt-2">Tests</div>
        <p className="text-[11px] muted">
          Check the response on every run. A failed test fails the step — and on a <strong>scheduled</strong> run that
          triggers the flow's Slack / email alerts.
        </p>
        <AssertionEditor
          rows={step.assertions ?? []}
          onChange={(rows) => onChange({ ...step, assertions: rows.length ? rows : undefined })}
          lastRunStep={lastRunStep}
        />
      </div>
    </div>
  );
}

// Assertions ("tests") editor. Each row reads a value from the response
// (status / latency / header / JSON path) and checks it. Failed tests
// fail the step → scheduled runs alert.
const ASSERT_OPS: Array<{ v: AssertOp; label: string }> = [
  { v: "eq", label: "equals" },
  { v: "ne", label: "not equals" },
  { v: "contains", label: "contains" },
  { v: "not_contains", label: "not contains" },
  { v: "gt", label: ">" },
  { v: "gte", label: "≥" },
  { v: "lt", label: "<" },
  { v: "lte", label: "≤" },
  { v: "exists", label: "exists" },
  { v: "not_exists", label: "is absent" },
];
function AssertionEditor({ rows, onChange, lastRunStep }: {
  rows: FlowAssertion[];
  onChange: (next: FlowAssertion[]) => void;
  lastRunStep: FlowRunStep | undefined;
}) {
  const results = (lastRunStep?.output as { assertions?: Array<{ ok: boolean; label: string; detail?: string }> } | null | undefined)?.assertions ?? [];
  const set = (i: number, patch: Partial<FlowAssertion>) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const needsValue = (op: AssertOp) => op !== "exists" && op !== "not_exists";
  return (
    <div className="space-y-1 min-w-0">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            className="input !text-sm font-mono flex-1 min-w-0"
            placeholder="$.status · $.duration_ms · $.body.id · $.headers.x"
            value={r.path}
            onChange={(e) => set(i, { path: e.target.value })}
            spellCheck={false}
          />
          <select className="input !text-sm w-32 shrink-0" value={r.op} onChange={(e) => set(i, { op: e.target.value as AssertOp })}>
            {ASSERT_OPS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          {needsValue(r.op) && (
            <input
              className="input !text-sm font-mono w-28 shrink-0"
              placeholder="value"
              value={r.value ?? ""}
              onChange={(e) => set(i, { value: e.target.value })}
              spellCheck={false}
            />
          )}
          <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} className="h-7 w-7 grid place-items-center rounded text-text-faint hover:text-danger hover:bg-surface-2 shrink-0" title="Remove">✕</button>
        </div>
      ))}
      {rows.length === 0 && (
        <div className="empty-card">No tests. Add one to assert on status, latency, headers, or response JSON.</div>
      )}
      <div className="pt-1">
        <button type="button" className="btn" onClick={() => onChange([...rows, { path: "$.status", op: "eq", value: "200" }])}>＋ Add test</button>
      </div>
      {results.length > 0 && (
        <details className="text-xs border border-border rounded p-2 bg-surface-2/50 mt-1">
          <summary className="cursor-pointer muted-strong font-semibold">
            Last run: {results.filter((a) => a.ok).length}/{results.length} passed
          </summary>
          <ul className="mt-1.5 space-y-1">
            {results.map((a, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className={a.ok ? "text-good" : "text-danger"}>{a.ok ? "✓" : "✗"}</span>
                <span className="font-mono">{a.label}{a.detail ? <span className="muted"> — {a.detail}</span> : null}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// Pluck-values-into-ctx editor. The "Picker" column scans the LAST run's
// response and lets the user click a leaf path to insert it directly.
function ExtractEditor({ rows, onChange, lastRunStep }: {
  rows: Array<{ as: string; from: string }>;
  onChange: (next: Array<{ as: string; from: string }>) => void;
  lastRunStep: FlowRunStep | undefined;
}) {
  const [pickerOpen, setPickerOpen] = useState<number | null>(null);
  const lastOutput = lastRunStep?.output as { stepCtx?: { body_json?: unknown; status?: number; headers?: Record<string, string> } } | undefined;
  const paths = useMemo(() => collectPaths(lastOutput?.stepCtx ?? null), [lastOutput?.stepCtx]);

  const set = (i: number, patch: Partial<{ as: string; from: string }>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const addRow = () => onChange([...rows, { as: "", from: "" }]);

  return (
    <div className="space-y-1 min-w-0">
      {rows.length > 0 && (
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)_60px_28px] gap-2 px-1 text-[11px] uppercase tracking-wider muted-strong">
          <div>As</div>
          <div>From</div>
          <div></div>
          <div></div>
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)_60px_28px] gap-2 items-center relative">
          <input
            className="input !text-sm font-mono"
            placeholder="token"
            value={r.as}
            onChange={(e) => set(i, { as: e.target.value })}
            spellCheck={false}
          />
          <input
            className="input !text-sm font-mono"
            placeholder="$.body.token"
            value={r.from}
            onChange={(e) => set(i, { from: e.target.value })}
            spellCheck={false}
          />
          <button
            type="button"
            className="btn !text-xs"
            disabled={paths.length === 0}
            onClick={() => setPickerOpen(pickerOpen === i ? null : i)}
            title={paths.length === 0 ? "Run the flow once to see paths" : "Pick a path from the last response"}
          >Pick</button>
          <button
            type="button"
            onClick={() => removeRow(i)}
            className="h-7 w-7 grid place-items-center rounded text-text-faint hover:text-danger hover:bg-surface-2"
            title="Remove"
          >✕</button>
          {pickerOpen === i && paths.length > 0 && (
            <div className="absolute z-20 right-12 top-full mt-1 card shadow-[var(--shadow-strong)] p-1 w-[420px] max-h-72 overflow-y-auto scroll-pane">
              <div className="px-2 py-1 text-[11px] muted-strong">Last response — click a path to insert</div>
              {paths.map((p) => (
                <button
                  key={p.path}
                  type="button"
                  className="w-full text-left px-2 py-1 rounded hover:bg-surface-2 text-xs flex items-center gap-2 min-w-0"
                  onMouseDown={(e) => { e.preventDefault(); set(i, { from: p.path }); setPickerOpen(null); }}
                >
                  <span className="font-mono truncate">{p.path}</span>
                  <span className="ml-auto muted text-[10px] truncate max-w-[180px]">{p.preview}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
      {rows.length === 0 && (
        <div className="empty-card">No extracts. Click <span className="empty-card-strong">+ Add extract</span> to pluck a value from the response.</div>
      )}
      <div className="pt-1">
        <button type="button" className="btn" onClick={addRow}>＋ Add extract</button>
      </div>
    </div>
  );
}

// Walks the step's last-run snapshot ({ body_json, status, headers }) and
// emits selectable JSONPath-ish strings + a short preview value. We cap
// the recursion depth + total path count so a huge response doesn't
// freeze the popover.
function collectPaths(stepCtx: { body_json?: unknown; status?: number; headers?: Record<string, string> } | null): Array<{ path: string; preview: string }> {
  if (!stepCtx) return [];
  const out: Array<{ path: string; preview: string }> = [];
  out.push({ path: "$.status", preview: String(stepCtx.status ?? "") });
  for (const [k, v] of Object.entries(stepCtx.headers ?? {})) {
    out.push({ path: `$.headers.${k}`, preview: String(v) });
  }
  walk(stepCtx.body_json ?? null, "$.body", 0);
  return out.slice(0, 200);

  function walk(v: unknown, path: string, depth: number) {
    if (out.length > 200 || depth > 6) return;
    if (v === null || typeof v !== "object") {
      out.push({ path, preview: previewVal(v) });
      return;
    }
    if (Array.isArray(v)) {
      out.push({ path, preview: `[${v.length}]` });
      v.slice(0, 8).forEach((item, idx) => walk(item, `${path}[${idx}]`, depth + 1));
      return;
    }
    out.push({ path, preview: "{…}" });
    let i = 0;
    for (const [k, vv] of Object.entries(v as Record<string, unknown>)) {
      if (i++ > 32) break;
      walk(vv, `${path}.${k}`, depth + 1);
    }
  }
}
function previewVal(v: unknown): string {
  if (v === null) return "null";
  if (typeof v === "string") return JSON.stringify(v).slice(0, 60);
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return typeof v;
}

// ── Script step editor ──────────────────────────────────────────────────

function ScriptStepEditor({ step, onChange, priorVars }: { step: Extract<FlowStep, { kind: "script" }>; onChange: (s: FlowStep) => void; priorVars: string[] }) {
  const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  const varsLine = priorVars.length ? `// extracted so far: ${priorVars.map((v) => "ctx." + v).join(", ")}\n` : "";
  return (
    <div className="space-y-2 min-w-0">
      <div className="rounded border border-border bg-surface-2 p-2 text-[11px] muted-strong font-mono whitespace-pre">
{`// ctx: shared state across steps — extracted values land at the top level (ctx.token, …)
${varsLine}// env: read-only env vars · steps: ctx.steps[stepId] = { status, headers, body_json, ok }
// log("hello"): append to step output
// Throw to fail the flow. Return false to halt successfully.`}
      </div>
      <div className="min-h-[260px]">
        <CodeMirror
          value={step.source}
          onChange={(v) => onChange({ ...step, source: v })}
          theme={isDark ? oneDark : githubLight}
          extensions={[javascript()]}
          height="260px"
          basicSetup={{
            lineNumbers: true, bracketMatching: true, closeBrackets: true,
            indentOnInput: true, autocompletion: false, foldGutter: false,
            highlightActiveLine: true,
          }}
          className="cm-host border border-border rounded"
        />
      </div>
      <p className="text-[11px] muted-strong">
        Scripts run synchronously inside the Worker. Soft timeout: 100ms (an infinite loop will still consume the
        Worker's CPU budget until the runtime kills it).
      </p>
    </div>
  );
}

// ── Delay step editor ───────────────────────────────────────────────────

function DelayStepEditor({ step, onChange }: { step: Extract<FlowStep, { kind: "delay" }>; onChange: (s: FlowStep) => void }) {
  return (
    <div className="space-y-2 min-w-0">
      <label className="text-xs">
        <span className="label">Wait (ms)</span>
        <input
          type="number"
          min={0}
          max={30000}
          step={50}
          className="input mt-1 !text-sm w-40"
          value={step.ms}
          onChange={(e) => onChange({ ...step, ms: Math.max(0, Math.min(30000, Math.trunc(Number(e.target.value) || 0))) })}
        />
      </label>
      <p className="text-[11px] muted">Capped at 30,000 ms.</p>
    </div>
  );
}

// ── Run pane ────────────────────────────────────────────────────────────

function RunPane({ outcome, pending, error, steps }: { outcome: RunOutcome | null; pending: boolean; error: string | null; steps: FlowStep[] }) {
  if (pending) {
    // Skeleton rows — one per enabled step — so a long flow reads as
    // "in progress" instead of a lone spinner.
    const active = steps.filter((s) => !s.disabled);
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm muted mb-3">
          <InlineLoading label={`Running ${active.length} step${active.length === 1 ? "" : "s"}…`} />
        </div>
        {active.map((s, i) => (
          <div key={s.id} className="rounded border border-border bg-surface-2/50 h-11 animate-pulse flex items-center px-3 gap-2" style={{ animationDelay: `${i * 120}ms` }}>
            <span className="chip bg-surface-3 muted-strong mono text-[10px]">{i + 1}</span>
            <span className="text-sm muted-strong">{s.name}</span>
          </div>
        ))}
      </div>
    );
  }
  if (!outcome && error) {
    return <div className="empty-card text-danger">Run failed: {error}</div>;
  }
  if (!outcome) {
    return (
      <div className="empty-card">
        Press <span className="empty-card-strong">Run</span> in the header to execute this flow.
        Each step's status, output, and extracted values land here — and every run is kept in History.
      </div>
    );
  }
  return <RunOutcomeView outcome={outcome} steps={steps} />;
}

// Shared render for a run outcome — used inline in the Run tab AND
// inside the history modal so both views stay in sync visually.
function RunOutcomeView({ outcome, steps }: { outcome: RunOutcome; steps: FlowStep[] }) {
  // Pull just the user-visible payload out of each step's recorded
  // output: the `extracted` map. Steps that didn't extract anything
  // are skipped so the panel doesn't look noisy when only one step
  // declared an `extract` block.
  const extracted = useMemo(() => {
    const out: Array<{ stepId: string; stepName: string; values: Record<string, unknown> }> = [];
    for (const s of outcome.steps) {
      const o = s.output as { extracted?: Record<string, unknown> } | null | undefined;
      if (o?.extracted && Object.keys(o.extracted).length > 0) {
        out.push({ stepId: s.step_id, stepName: s.step_name, values: o.extracted });
      }
    }
    return out;
  }, [outcome.steps]);
  const [showRawCtx, setShowRawCtx] = useState(false);
  return (
    <div className="space-y-3 min-w-0">
      <div className="flex items-center gap-3">
        <RunStatusPill status={outcome.run.status} />
        <span className="text-xs muted-strong">{outcome.run.duration_ms ?? 0} ms · {outcome.run.total_steps} steps</span>
        {outcome.run.error && <span className="text-xs text-danger truncate" title={outcome.run.error}>{outcome.run.error}</span>}
      </div>
      <ol className="space-y-2">
        {outcome.steps.map((s) => {
          const stepDef = steps.find((d) => d.id === s.step_id);
          return <RunStepCard key={s.id} step={s} disabled={!!stepDef?.disabled} />;
        })}
      </ol>
      {extracted.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-xs uppercase tracking-wider muted-strong font-semibold">Extracted values</div>
          <div className="rounded border border-border bg-surface-2 divide-y divide-border">
            {extracted.map((g) => (
              <div key={g.stepId} className="p-2.5 space-y-1.5">
                <div className="text-[11px] muted-strong">{g.stepName}</div>
                <table className="w-full text-xs">
                  <tbody>
                    {Object.entries(g.values).map(([k, v]) => (
                      <tr key={k} className="align-top">
                        <th className="text-left font-mono font-normal text-text-dim pr-3 py-0.5 w-[35%] break-all">{k}</th>
                        <td className="font-mono text-text py-0.5 break-all">{prettyExtractedValue(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}
      {outcome.run.final_context != null && (
        <details className="border-t border-border pt-2" open={showRawCtx} onToggle={(e) => setShowRawCtx((e.target as HTMLDetailsElement).open)}>
          <summary className="text-[11px] uppercase tracking-wider muted-strong font-semibold cursor-pointer select-none">
            Raw ctx (debug)
          </summary>
          <div className="mt-1.5">
            <CodeViewer value={JSON.stringify(outcome.run.final_context, null, 2)} lang="json" maxHeight="320px" />
          </div>
        </details>
      )}
    </div>
  );
}

function prettyExtractedValue(v: unknown): string {
  if (v == null) return String(v);
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try { return JSON.stringify(v); } catch { return String(v); }
}

function RunStepCard({ step, disabled }: { step: FlowRunStep; disabled: boolean }) {
  const [open, setOpen] = useState(step.status === "failed");
  const assertions = (step.output as { assertions?: Array<{ ok: boolean; label: string; detail?: string }> } | null | undefined)?.assertions ?? [];
  const passed = assertions.filter((a) => a.ok).length;
  return (
    <li className="rounded border border-border bg-surface-2/40">
      <button
        type="button"
        className="w-full flex items-center gap-3 px-3 py-2 text-left min-w-0"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="chip bg-surface-3 muted-strong mono shrink-0 text-[10px]">{step.step_index + 1}</span>
        <StepKindIcon kind={step.step_kind as FlowStep["kind"]} />
        <span className="text-sm flex-1 min-w-0 truncate">{step.step_name}</span>
        {disabled && <span className="chip bg-surface-3 muted-strong text-[10px]">disabled</span>}
        {assertions.length > 0 && (
          <span className={"chip text-[10px] " + (passed === assertions.length ? "bg-good/15 text-good" : "bg-danger/15 text-danger")} title="Test results">
            {passed}/{assertions.length} tests
          </span>
        )}
        <span className="text-xs muted-strong shrink-0">{step.duration_ms ?? 0} ms</span>
        <RunStepStatusPill status={step.status} />
      </button>
      {open && (
        <div className="border-t border-border px-3 py-2 space-y-2">
          {step.error && <div className="text-xs text-danger font-mono break-all">{step.error}</div>}
          {assertions.length > 0 && (
            <ul className="text-xs space-y-1">
              {assertions.map((a, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className={a.ok ? "text-good" : "text-danger"}>{a.ok ? "✓" : "✗"}</span>
                  <span className="font-mono">{a.label}{a.detail ? <span className="muted"> — {a.detail}</span> : null}</span>
                </li>
              ))}
            </ul>
          )}
          {step.output != null && (
            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-wider muted-strong">Output</div>
              <CodeViewer value={JSON.stringify(step.output, null, 2)} lang="json" maxHeight="240px" />
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function RunStatusPill({ status }: { status: FlowRun["status"] }) {
  const map: Record<FlowRun["status"], string> = {
    running: "bg-accent-soft text-accent",
    succeeded: "bg-good/15 text-good",
    failed: "bg-danger/15 text-danger",
    cancelled: "bg-surface-3 muted-strong",
  };
  return <span className={"chip " + map[status]}>{status}</span>;
}
function RunStepStatusPill({ status }: { status: FlowRunStep["status"] }) {
  const map: Record<FlowRunStep["status"], string> = {
    pending: "bg-surface-3 muted-strong",
    running: "bg-accent-soft text-accent",
    succeeded: "bg-good/15 text-good",
    failed: "bg-danger/15 text-danger",
    skipped: "bg-surface-3 muted-strong",
  };
  return <span className={"chip text-[10px] " + map[status]}>{status}</span>;
}

// ── History pane ────────────────────────────────────────────────────────

function HistoryPane({ flowId, onOpen }: { flowId: string; onOpen: (r: RunOutcome) => void }) {
  const runsQ = useQuery({ queryKey: ["flow-runs", flowId], queryFn: () => api.flows.runs(flowId, 50) });
  const [loadingId, setLoadingId] = useState<string | null>(null);
  if (runsQ.isLoading) return <InlineLoading label="Loading history…" />;
  const runs = runsQ.data?.runs ?? [];
  if (runs.length === 0) return <div className="empty-card">No runs yet. Press <span className="empty-card-strong">Run</span> to execute.</div>;
  return (
    <ul className="space-y-1">
      {runs.map((r) => (
        <li key={r.id}>
          <button
            type="button"
            className="w-full flex items-center gap-3 px-2 py-1.5 rounded hover:bg-surface-2 text-left min-w-0"
            onClick={async () => {
              setLoadingId(r.id);
              try {
                const full = await api.flows.runDetail(flowId, r.id);
                onOpen(full);
              } finally {
                setLoadingId(null);
              }
            }}
          >
            <RunStatusPill status={r.status} />
            <span className="text-xs muted-strong">{new Date(r.started_at).toLocaleString()}</span>
            <span className="text-xs muted ml-auto shrink-0">{r.duration_ms ?? 0} ms · {r.total_steps} steps</span>
            {loadingId === r.id && <InlineLoading />}
          </button>
        </li>
      ))}
    </ul>
  );
}

// ── Docs ────────────────────────────────────────────────────────────────

function DocsEditor({ docs, setDocs }: { docs: string; setDocs: (s: string) => void }) {
  const [mode, setMode] = useState<"edit" | "preview" | "split">("split");
  return (
    <div className="space-y-3 h-full flex flex-col min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="label">Docs (Markdown)</label>
        <div className="ml-auto flex gap-1">
          {(["edit", "split", "preview"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={
                "px-2 py-0.5 rounded-sm text-xs font-medium " +
                (m === mode ? "bg-accent text-[var(--ink-on-accent)]" : "bg-surface-2 text-text-dim hover:bg-surface-3 hover:text-text")
              }
            >{m}</button>
          ))}
        </div>
      </div>
      <div className={"flex-1 min-h-[240px] min-w-0 grid gap-3 " + (mode === "split" ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1")}>
        {mode !== "preview" && (
          <textarea
            className="input input-mono min-h-[240px] min-w-0 w-full"
            value={docs}
            onChange={(e) => setDocs(e.target.value)}
            placeholder="Document what this flow does."
            spellCheck={false}
          />
        )}
        {mode !== "edit" && (
          <div className="rounded border border-border bg-surface-2 p-3 overflow-auto scroll-pane min-h-[240px] min-w-0">
            {docs ? <Markdown source={docs} /> : <p className="text-sm muted italic">Nothing to preview yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Add-step menu ───────────────────────────────────────────────────────

function AddStepMenu({ onAdd }: { onAdd: (kind: FlowStep["kind"]) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        title="Add step"
        aria-label="Add step"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        className="h-6 w-6 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
      >＋</button>
      {open && (
        <div className="absolute right-0 top-full mt-1 card shadow-[var(--shadow-strong)] p-1 z-40 text-sm w-44" role="menu">
          <button className="w-full text-left px-2 py-1 rounded hover:bg-surface-2" onMouseDown={(e) => { e.preventDefault(); onAdd("request"); setOpen(false); }}>Add request step</button>
          <button className="w-full text-left px-2 py-1 rounded hover:bg-surface-2" onMouseDown={(e) => { e.preventDefault(); onAdd("script"); setOpen(false); }}>Add script step</button>
          <button className="w-full text-left px-2 py-1 rounded hover:bg-surface-2" onMouseDown={(e) => { e.preventDefault(); onAdd("delay"); setOpen(false); }}>Add delay step</button>
        </div>
      )}
    </div>
  );
}

// ── Env picker (simple select) ──────────────────────────────────────────

// ── Misc ────────────────────────────────────────────────────────────────

// First-open welcome card for the Step tab. Replaces the terse "no step
// selected" empty state with a what-is-this primer + add buttons.
function FlowWelcome({ onAdd, hasSteps }: { onAdd: (kind: FlowStep["kind"]) => void; hasSteps: boolean }) {
  if (hasSteps) {
    return <div className="empty-card">Select a step on the left to edit it.</div>;
  }
  return (
    <div className="card p-5 max-w-lg space-y-4">
      <h3 className="font-semibold">Build your first flow</h3>
      <p className="text-sm muted">
        A flow chains steps that run in order — call an API, pass values from one response into the
        next request, assert on the results. Run it here, on a schedule, or from CI.
      </p>
      <div className="space-y-2">
        {([
          { kind: "request" as const, label: "Request", desc: "Call an API — saved or inline. Extract values for later steps." },
          { kind: "script" as const, label: "Script", desc: "Transform data or assert with a JS snippet." },
          { kind: "delay" as const, label: "Delay", desc: "Wait between steps (e.g. for a webhook to land)." },
        ]).map(({ kind, label, desc }) => (
          <button
            key={kind}
            type="button"
            className="w-full text-left rounded border border-border hover:border-border-strong hover:bg-surface-2 px-3 py-2.5 flex items-center gap-3 transition-colors"
            onClick={() => onAdd(kind)}
          >
            <StepKindIcon kind={kind} />
            <span className="min-w-0">
              <span className="block text-sm font-medium">{label}</span>
              <span className="block text-xs muted">{desc}</span>
            </span>
            <span className="ml-auto text-text-faint">＋</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// Variable names extracted by request steps BEFORE the given step —
// these are exactly what {{name}} can reference in URLs/headers/bodies
// and what scripts see as ctx.<name>.
function priorExtractVars(steps: FlowStep[], beforeStepId: string): string[] {
  const vars: string[] = [];
  for (const s of steps) {
    if (s.id === beforeStepId) break;
    if (s.kind === "request") {
      for (const ex of s.extract ?? []) {
        if (ex.as.trim() && !vars.includes(ex.as.trim())) vars.push(ex.as.trim());
      }
    }
  }
  return vars;
}

// Compact key/value editor used by inline request steps for headers +
// query params. Collapsed to a single "+ Add" affordance when empty.
function KVRows({ label, rows, onChange, keyPlaceholder, valuePlaceholder }: {
  label: string;
  rows: KV[];
  onChange: (rows: KV[]) => void;
  keyPlaceholder: string;
  valuePlaceholder: string;
}) {
  const set = (i: number, patch: Partial<KV>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="label">{label}</span>
        <button type="button" className="btn !text-xs !py-0.5" onClick={() => onChange([...rows, { key: "", value: "" }])}>＋ Add</button>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_28px] gap-2">
          <input
            className="input !text-sm font-mono"
            placeholder={keyPlaceholder}
            value={r.key}
            onChange={(e) => set(i, { key: e.target.value })}
            spellCheck={false}
          />
          <input
            className="input !text-sm font-mono"
            placeholder={valuePlaceholder}
            value={r.value}
            onChange={(e) => set(i, { value: e.target.value })}
            spellCheck={false}
          />
          <button
            type="button"
            onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
            className="h-7 w-7 grid place-items-center rounded text-text-faint hover:text-danger hover:bg-surface-2"
            title="Remove"
          >✕</button>
        </div>
      ))}
    </div>
  );
}

// "Did my extracts actually work?" — shows the values this step pulled
// out on the most recent run, right under the extract editor.
function ExtractPreview({ lastRunStep }: { lastRunStep: FlowRunStep | undefined }) {
  const extracted = (lastRunStep?.output as { extracted?: Record<string, unknown> } | null | undefined)?.extracted;
  if (!extracted || Object.keys(extracted).length === 0) return null;
  return (
    <details className="text-xs border border-border rounded p-2 bg-surface-2/50">
      <summary className="cursor-pointer muted-strong font-semibold">Last run extracted {Object.keys(extracted).length} value{Object.keys(extracted).length === 1 ? "" : "s"}</summary>
      <div className="mt-1.5 space-y-1 max-h-40 overflow-y-auto scroll-pane">
        {Object.entries(extracted).map(([k, v]) => (
          <div key={k} className="flex items-start gap-2 min-w-0">
            <span className="font-mono text-text-dim shrink-0">{k}:</span>
            <span className="font-mono text-text break-all">{prettyExtractedValue(v)}</span>
          </div>
        ))}
      </div>
    </details>
  );
}

function newStepId(): string {
  // Browser-side equivalent of the server's id helper — short random
  // suffix so collisions inside a single flow are astronomically rare.
  // We only need uniqueness within a flow's step list.
  const arr = new Uint8Array(8);
  crypto.getRandomValues(arr);
  return "stp_" + Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
}

// Order-independent JSON serializer used for dirty-checking — recursively
// sorts object keys so { a: 1, b: 2 } and { b: 2, a: 1 } compare equal.
// Arrays preserve order (they encode user-meaningful sequence).
function stableStringify(v: unknown): string {
  const seen = new WeakSet();
  const sort = (x: unknown): unknown => {
    if (x === null || typeof x !== "object") return x;
    if (seen.has(x as object)) return null;
    seen.add(x as object);
    if (Array.isArray(x)) return x.map(sort);
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(x as Record<string, unknown>).sort()) {
      out[k] = sort((x as Record<string, unknown>)[k]);
    }
    return out;
  };
  return JSON.stringify(sort(v));
}

